/**
 * Lesson MCQ Editor
 * Three-tab editor: Lesson (content + Page), Questions (MCQ set), Configuration
 */

import * as React from 'react';
import { cn } from '@ai-content-hub/content-kit';
import {
    CompositeEditShell,
    CompositeEditContentTab,
    CompositeEditQuestionsLayout,
    type EditTabDescriptor,
    type ContentEditProps,
    useLocale,
} from '@ai-content-hub/content-core';
import {
    PageEdit,
    MCQSetEdit,
    type PageElementData,
    type MCQSetData,
} from '@ai-content-hub/content-elements';
import type { LessonMCQData, LessonMCQConfig } from './schema';
import { defaultLessonMCQData } from './schema';
import { deriveLessonTitle } from './title';
import { BookOpen, ListChecks, Settings } from 'lucide-react';

type LessonMCQEditTab = 'lesson' | 'questions' | 'config';

export type LessonMCQEditProps = ContentEditProps<LessonMCQData, LessonMCQConfig> & {
    className?: string;
};

export const LessonMCQEdit: React.FC<LessonMCQEditProps> = ({
    value: data,
    onChange,
    config,
    onConfigChange: _onConfigChange,
    disabled = false,
    editorOptions,
    configSlot,
    errors,
    className,
    language,
}) => {
    const [activeTab, setActiveTab] = React.useState<LessonMCQEditTab>('lesson');
    const { t, language: activeLanguage } = useLocale({
        config: config?.i18n,
        initialLanguage: language,
    });

    const safeData: LessonMCQData = {
        ...defaultLessonMCQData,
        ...data,
        lessonContent: data?.lessonContent ?? defaultLessonMCQData.lessonContent,
        questionSet: data?.questionSet ?? defaultLessonMCQData.questionSet,
    };

    const questionCount = safeData.questionSet?.items?.length ?? 0;

    const updateData = (updates: Partial<LessonMCQData>) => {
        onChange({
            ...safeData,
            ...updates,
        });
    };

    // EAA-2031: a generated lesson can arrive with an empty required title; fill it once
    // from the set / page / description so the record can be saved as generated.
    const hasTitle = Boolean(safeData.title?.trim());
    const derivedTitle = hasTitle ? '' : deriveLessonTitle(safeData);
    React.useEffect(() => {
        if (disabled || hasTitle || !derivedTitle) return;
        updateData({
            title: derivedTitle,
            lessonContent: { ...safeData.lessonContent, title: derivedTitle },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps -- fill once per empty title
    }, [hasTitle, derivedTitle, disabled]);

    const handleLessonChange = (lessonContent: PageElementData) => {
        updateData({
            lessonContent,
            title: lessonContent.title ?? safeData.title,
            description: lessonContent.description ?? safeData.description,
        });
    };

    const handleQuestionsChange = (questionSet: MCQSetData) => {
        updateData({ questionSet });
    };

    const lessonMCQTabs: EditTabDescriptor[] = [
        { id: 'lesson', label: t('lessonTab'), icon: BookOpen },
        {
            id: 'questions',
            label: t('questionsTab'),
            icon: ListChecks,
            badge: questionCount,
        },
        ...(configSlot ? [{ id: 'config', label: t('configuration'), icon: Settings }] : []),
    ];

    const questionSetError = errors?.questionSet;

    return (
        <CompositeEditShell
            prefix="lesson-mcq-edit"
            tabs={lessonMCQTabs}
            activeTab={activeTab}
            onTabChange={(id) => setActiveTab(id as LessonMCQEditTab)}
            configSlot={configSlot}
            className={cn('lesson-mcq-edit h-full min-h-0', className)}
            renderPanel={(tabId) => {
                if (tabId === 'lesson') {
                    if (
                        editorOptions?.showTitle === false &&
                        editorOptions?.showDescription === false
                    ) {
                        return (
                            <div className="lesson-mcq-edit-lesson-panel flex-1 min-h-0 overflow-y-auto px-1">
                                <PageEdit
                                    value={safeData.lessonContent}
                                    onChange={handleLessonChange}
                                    disabled={disabled}
                                    hideSettings
                                    config={config}
                                    language={activeLanguage}
                                    className="lesson-mcq-edit-page-inner"
                                />
                            </div>
                        );
                    }

                    return (
                        <CompositeEditContentTab
                            prefix="lesson-mcq-edit"
                            disabled={disabled}
                            title={{
                                id: 'lesson-mcq-title',
                                label: t('title'),
                                value: safeData.title ?? '',
                                onChange: (v) =>
                                    updateData({
                                        title: v,
                                        lessonContent: {
                                            ...safeData.lessonContent,
                                            title: v,
                                        },
                                    }),
                                placeholder: t('enterTitlePlaceholder'),
                                required: true,
                            }}
                            titleInputClassName="lesson-mcq-edit-title-input"
                            description={{
                                id: 'lesson-mcq-description',
                                label: t('descriptionOptional'),
                                value: safeData.description ?? '',
                                onChange: (v) =>
                                    updateData({
                                        description: v,
                                        lessonContent: {
                                            ...safeData.lessonContent,
                                            description: v,
                                        },
                                    }),
                                placeholder: t('enterDescriptionPlaceholder'),
                                rows: 3,
                            }}
                            descriptionInputClassName="lesson-mcq-edit-description-input"
                            page={
                                <PageEdit
                                    value={safeData.lessonContent}
                                    onChange={handleLessonChange}
                                    disabled={disabled}
                                    hideSettings
                                    config={config}
                                    language={activeLanguage}
                                    className="lesson-mcq-edit-page-inner"
                                />
                            }
                        />
                    );
                }

                if (tabId === 'questions') {
                    return (
                        <CompositeEditQuestionsLayout
                            variant="detail-only"
                            prefix="lesson-mcq-edit"
                            detail={
                                <>
                                    {questionSetError ? (
                                        <p
                                            className="lesson-mcq-edit-questions-error text-sm text-destructive mb-3"
                                            role="alert"
                                        >
                                            {questionSetError}
                                        </p>
                                    ) : null}
                                    <MCQSetEdit
                                        value={safeData.questionSet}
                                        onChange={handleQuestionsChange}
                                        disabled={disabled}
                                        errors={errors}
                                        config={config}
                                        language={activeLanguage}
                                        className="lesson-mcq-edit-mcq-set-inner"
                                    />
                                </>
                            }
                        />
                    );
                }

                return null;
            }}
        />
    );
};

export default LessonMCQEdit;
